import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { AccessModes, Dimensions, SortOrder, Sources } from 'components/insights/constants';
import CircularProgress from 'components/shared/CircularProgress';
import {
  buildMobileGridTemplateColumns,
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
} from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import { buildGridTemplateTracks } from 'components/shared/table/buildGridTemplateColumns';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { simpleDate } from 'lib/date';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import {
  getBusinessDevelopmentAnalyticsFilters,
  remapReferralsFilters,
  useBusinessDevelopmentStore,
} from 'stores/businessDevelopmentStore';
import { serializeSort } from 'stores/helpers';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import BusinessDevelopmentApiExport from '../BusinessDevelopmentApiExport';
import { entityFilterForViewBy, pivotSlug, TablePivot, ViewBy, viewBySlug } from '../constants';

// --- Pivot-specific configuration ---

type PivotConfig = {
  source: Sources;
  dimensions: Dimensions[];
  urlDimension: Dimensions;
  snfDimension: Dimensions;
  providerDimension: Dimensions;
  dateColumnHeader: string;
  title: string;
  exportSlug: string;
};

const PIVOT_CONFIGS: Record<TablePivot, PivotConfig> = {
  patientVolume: {
    source: Sources.INSIGHTS_CARE_TRANSITIONS,
    dimensions: [
      Dimensions.PATIENT_NAME,
      Dimensions.EPISODE_URL,
      Dimensions.PREDECESSOR_GROUP,
      Dimensions.GROUP,
      Dimensions.DATE,
    ],
    urlDimension: Dimensions.EPISODE_URL,
    snfDimension: Dimensions.PREDECESSOR_GROUP,
    providerDimension: Dimensions.GROUP,
    dateColumnHeader: 'Admission Date',
    title: 'Patients',
    exportSlug: 'patient-volume',
  },
  discharges: {
    source: Sources.LOCATION_EPISODE_DISCHARGES,
    dimensions: [
      Dimensions.PATIENT_NAME,
      Dimensions.EPISODE_URL,
      Dimensions.PREDECESSOR_GROUP,
      Dimensions.GROUP_NAME,
      Dimensions.DATE,
    ],
    urlDimension: Dimensions.EPISODE_URL,
    snfDimension: Dimensions.PREDECESSOR_GROUP,
    providerDimension: Dimensions.GROUP_NAME,
    dateColumnHeader: 'Discharge Date',
    title: 'Patients',
    exportSlug: 'discharges',
  },
  referrals: {
    source: Sources.INSIGHTS_REFERRALS,
    dimensions: [
      Dimensions.PATIENT_NAME,
      Dimensions.REFERRAL_URL,
      Dimensions.SENDING_GROUP,
      Dimensions.RECEIVING_GROUP,
      Dimensions.DATE,
    ],
    urlDimension: Dimensions.REFERRAL_URL,
    snfDimension: Dimensions.SENDING_GROUP,
    providerDimension: Dimensions.RECEIVING_GROUP,
    dateColumnHeader: 'Referral Created Date',
    title: 'Referrals',
    exportSlug: 'referrals',
  },
};

// --- Row type and column builder ---

type PatientRow = {
  patientName: string;
  url: string;
  snf: string;
  provider: string;
  date: string;
};

const columnHelper = createColumnHelper<PatientRow>();

function buildColumns(config: PivotConfig, groupTypeName: string) {
  return [
    columnHelper.accessor('patientName', {
      id: Dimensions.PATIENT_NAME,
      header: 'Patient Name',
      cell: (info) => {
        const name = info.getValue();
        const { url } = info.row.original;
        if (!name) return '\u2014';
        if (!url) return name;
        return (
          <PatientLink href={url} target='_blank' rel='noopener noreferrer'>
            {name}
          </PatientLink>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('snf', {
      id: config.snfDimension,
      header: 'SNF',
      cell: (info) => info.getValue() || '\u2014',
      enableSorting: true,
    }),
    columnHelper.accessor('provider', {
      id: config.providerDimension,
      header: groupTypeName,
      cell: (info) => info.getValue() || '\u2014',
      enableSorting: true,
    }),
    columnHelper.accessor('date', {
      id: Dimensions.DATE,
      header: config.dateColumnHeader,
      cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '\u2014'),
      enableSorting: true,
    }),
  ];
}

// --- Component ---

type BusinessDevelopmentPatientListProps = {
  tablePivot: TablePivot;
  viewBy: ViewBy;
  entityId: string;
  entityName: string;
};

export default function BusinessDevelopmentPatientList({
  tablePivot,
  viewBy,
  entityId,
  entityName,
}: BusinessDevelopmentPatientListProps) {
  const config = PIVOT_CONFIGS[tablePivot];

  const extraFilters = useMemo(
    () => entityFilterForViewBy(viewBy, entityId, entityName),
    [viewBy, entityId, entityName]
  );

  const baseParams = useMemo(
    () => ({
      source: config.source,
      accessModes: [AccessModes.MANAGER],
      dimensions: config.dimensions,
      metrics: [],
    }),
    [config]
  );

  const exportParams = useMemo(
    () => ({
      ...baseParams,
      sortBy: `${Dimensions.DATE} ${SortOrder.DESC}`,
      dimensionDisplayNames: {
        [config.snfDimension]: 'SNF',
        [config.providerDimension]: 'Provider',
        [Dimensions.DATE]: config.dateColumnHeader,
      },
    }),
    [baseParams, config]
  );

  const filename = `${viewBySlug(viewBy)}-entity-${entityName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-patients-by-${pivotSlug(tablePivot)}`;

  return (
    <Wrapper>
      <Header>
        <Title>{config.title}</Title>
        <BusinessDevelopmentApiExport params={exportParams} filename={filename} extraFilters={extraFilters} />
      </Header>
      <ExpandableArea mobileFlush>
        <PatientListTable tablePivot={tablePivot} config={config} baseParams={baseParams} extraFilters={extraFilters} />
      </ExpandableArea>
    </Wrapper>
  );
}

// --- Inner table ---

function PatientListTable({
  tablePivot,
  config,
  baseParams,
  extraFilters,
}: {
  tablePivot: TablePivot;
  config: PivotConfig;
  baseParams: Record<string, unknown>;
  extraFilters: Record<string, unknown>;
}) {
  const groupTypeName = useBusinessDevelopmentStore((s) => s.selectedGroupType!.displayName);

  const storeFilters = useBusinessDevelopmentStore((state) => getBusinessDevelopmentAnalyticsFilters(state));
  const baseFilters = useMemo(() => {
    if (!storeFilters) return null;
    // Referrals source uses different filter key names than care_transitions/discharges.
    if (config.source === Sources.INSIGHTS_REFERRALS) {
      // transferredFromClient (if present in extraFilters via entityFilterForViewBy) passes through
      // in ...rest after remapReferralsFilters, since the referrals source accepts it directly.
      return { ...remapReferralsFilters(storeFilters), ...extraFilters };
    }
    return { ...storeFilters, ...extraFilters };
  }, [storeFilters, extraFilters, config.source]);

  const [sort, setSort] = useState<SortingState>([{ id: Dimensions.DATE, desc: true }]);

  const queryParams = useMemo(
    () => ({
      ...baseParams,
      sortBy: serializeSort(sort),
      pageSize: 10,
    }),
    [baseParams, sort]
  );

  const query = useInfiniteQuery({
    queryKey: ['bd-slideout-patient-list', tablePivot, queryParams, baseFilters],
    queryFn: ({ pageParam }) =>
      retrieveAnalytics(
        pageParam ? { ...queryParams, ...(pageParam as Record<string, unknown>) } : queryParams,
        baseFilters
      ),
    enabled: !!baseFilters,
    placeholderData: keepPreviousData,
    initialPageParam: undefined as Record<string, unknown> | undefined,
    getNextPageParam: (lastPage: Analytics) =>
      (lastPage?.links?.next?.payload as Record<string, unknown> | undefined) ?? undefined,
  });

  const rows = useMemo<PatientRow[]>(() => {
    const pages = query.data?.pages ?? [];
    if (!pages.length) return [];
    const headers = pages[0]?.dimensionHeaders ?? [];
    const idx = (name: string) => headers.findIndex((h) => h.name === name);
    const patientNameIdx = idx(Dimensions.PATIENT_NAME);
    const urlIdx = idx(config.urlDimension);
    const snfIdx = idx(config.snfDimension);
    const providerIdx = idx(config.providerDimension);
    const dateIdx = idx(Dimensions.DATE);
    return pages.flatMap((page) =>
      (page?.data ?? []).map((row): PatientRow => {
        const dimValue = (i: number) => (i >= 0 ? row.dimensionValues[i]?.value : undefined);
        return {
          patientName: String(dimValue(patientNameIdx) ?? ''),
          url: String(dimValue(urlIdx) ?? ''),
          snf: String(dimValue(snfIdx) ?? ''),
          provider: String(dimValue(providerIdx) ?? ''),
          date: String(dimValue(dateIdx) ?? ''),
        };
      })
    );
  }, [query.data, config]);

  const { customRef, isIntersecting } = useIntersectionObserver({ threshold: 0.2 });

  useEffect(() => {
    if (isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [isIntersecting, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally specific deps to avoid re-running on every query reference change

  const columns = useMemo(() => buildColumns(config, groupTypeName), [config, groupTypeName]);

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const showEmpty = !query.isLoading && rows.length === 0;
  const showNextSpinner = query.isFetchingNextPage;

  const visibleColumns = table.getVisibleLeafColumns();
  const tracks = buildGridTemplateTracks(visibleColumns);

  return (
    <ScrollArea>
      <StyledTable
        $theme={DataTableTheme.DEFAULT}
        $freezeFirstColumn
        $gridTemplateColumns={tracks.join(' ')}
        $mobileGridTemplateColumns={
          visibleColumns.length ? buildMobileGridTemplateColumns(tracks, visibleColumns[0].getSize()) : undefined
        }>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} width={cell.column.getSize()}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <SentinelRow ref={customRef} />
          {showNextSpinner && (
            <TableRow>
              <SpinnerCell colSpan={columns.length}>
                <CircularProgress size={16} thickness={2} />
              </SpinnerCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>
      {showEmpty && <EmptyMessage>No rows found</EmptyMessage>}
    </ScrollArea>
  );
}

// --- Styled components ---

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const ScrollArea = styled.div`
  max-height: 520px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;

  @media (max-width: 800px) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const SentinelRow = styled.tr`
  display: block;
  height: 1px;
`;

const SpinnerCell = styled.td`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.primaryBlue};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const PatientLink = styled.a`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyMessage = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black50};
`;
